import { useEffect, useRef, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Button as RoccoButton } from '@rocco/components/button';
import {
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '@rocco/components/sheet';
import type {
    FiltersConfig,
    ModalFiltersConfigSection,
} from '@rocco/ui/filter-sort/types/filter-config-section';
import type { AggregationArgs } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import type { FiltersModalCategoryTreeConfig } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModal';
import { FiltersModalCategoryTree } from '@rocco/ui/filter-sort/components/filters-modal/refine/FiltersModalCategoryTree';
import { RefineChipsRow } from '@rocco/ui/filter-sort/components/filters-modal/refine/RefineChipsRow';
import { RefineDrillInView } from '@rocco/ui/filter-sort/components/filters-modal/refine/RefineDrillInView';
import { RefineSectionList } from '@rocco/ui/filter-sort/components/filters-modal/refine/RefineSectionList';
import { RefineSortPills } from '@rocco/ui/filter-sort/components/filters-modal/refine/RefineSortPills';
import {
    RefineBrowseModePills,
    type RefineBrowseModeConfig,
} from '@rocco/ui/filter-sort/components/filters-modal/refine/RefineBrowseModePills';
import { getRefineSectionPresentation } from '@rocco/ui/filter-sort/components/filters-modal/refine/get-refine-presentation';
import {
    MORE_FILTERS_TITLE,
    type RefineSubView,
} from '@rocco/ui/filter-sort/components/filters-modal/refine/refine-view';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import {
    isCategoryFilterState,
    isSearchableListFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type { FilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type {
    FilterStates,
    FilterStatesKey,
} from '@rocco/ui/filter-sort/view-models/filter-states';
import type { SortOption } from '@rocco/types/enum/sort-option';
import { cn } from '@rocco/utils/cn';
import { formatToThousandsDelimiter } from '@rocco/utils/format-number';
import { pluralize } from '@rocco/utils/string-helpers';

export interface FiltersModalRefineProps {
    config: FiltersConfig;
    /** Runtime facet sections, synthesised from the staged states. */
    facetSections: ModalFiltersConfigSection[];
    localFilterStates: FilterStates;
    isLoading: boolean;
    showSorting: boolean;
    categoryTree?: FiltersModalCategoryTreeConfig;
    /** Products/Brands switch. Omitted by entities with a single browse mode. */
    browseMode?: RefineBrowseModeConfig;
    /**
     * Overrides the noun in the apply CTA. The config's resultName is fixed per
     * filter config, so a page that can switch what it lists (products vs
     * brands) has to say which it is currently counting.
     */
    resultName?: string;
    onUpdateFilterState: (
        filterKey: FilterStatesKey,
        newState: FilterState,
        shouldAggregate?: boolean,
    ) => void;
    onUpdateFacetFilterState: (
        filterKey: FilterStatesKey,
        newState: FilterState,
        shouldAggregate?: boolean,
    ) => void;
    onAlphabeticalGrouping: (args: AggregationArgs) => void;
    onStageSort: (sort: SortOption) => void;
    onStageCategory: (url: string) => void;
    onClearFilters: () => void;
    onApply: () => void;
}

/**
 * The decision-support layout of the mobile filter sheet: selected chips,
 * staged category tree, inline sort pills and per-data-shape controls above
 * a single full-width commit CTA. Large lists open in-sheet sub-views via
 * an internal view stack; all interactions stage into the sheet's local
 * filter states and only the CTA commits.
 */
export const FiltersModalRefine = (props: FiltersModalRefineProps) => {
    const {
        config,
        facetSections,
        localFilterStates,
        isLoading,
        showSorting,
        categoryTree,
        browseMode,
        resultName,
        onUpdateFilterState,
        onUpdateFacetFilterState,
        onAlphabeticalGrouping,
        onStageSort,
        onStageCategory,
        onClearFilters,
        onApply,
    } = props;

    const [viewStack, setViewStack] = useState<RefineSubView[]>([]);
    const lastNavDirection = useRef<'push' | 'pop'>('push');
    const scrollRef = useRef<HTMLDivElement>(null);
    const { onSectionOpen } = useFiltersContext();

    const pushView = (view: RefineSubView) => {
        lastNavDirection.current = 'push';
        setViewStack((stack) => [...stack, view]);
        onSectionOpen?.(
            view.type === 'section' ? view.sectionTitle : MORE_FILTERS_TITLE,
        );
    };

    const popView = () => {
        lastNavDirection.current = 'pop';
        setViewStack((stack) => stack.slice(0, -1));
    };

    const resolveViewSections = (
        view: RefineSubView,
    ): { section: ModalFiltersConfigSection; isFacet: boolean }[] => {
        if (view.type === 'moreFilters') {
            return [
                ...config.modalFilters.map((section) => ({
                    section,
                    isFacet: false,
                })),
                ...facetSections.map((section) => ({
                    section,
                    isFacet: true,
                })),
            ].filter(
                ({ section }) =>
                    getRefineSectionPresentation(section, localFilterStates) ===
                    'moreFilters',
            );
        }

        const source = view.isFacet ? facetSections : config.modalFilters;
        const section = source.find(
            (candidate) => candidate.title === view.sectionTitle,
        );
        return section ? [{ section, isFacet: view.isFacet }] : [];
    };

    const currentView = viewStack.at(-1) ?? null;
    const currentSections = currentView ? resolveViewSections(currentView) : [];
    // The staged section vanished (e.g. a facet dropped by an aggregation
    // refresh) — fall back to the root view and trim the stack.
    const isSearchInFlight = [
        ...Object.values(localFilterStates.states),
        ...(localFilterStates.facet?.facetOptions?.map(
            (facet) => facet.state,
        ) ?? []),
    ].some(
        (state) =>
            state && isSearchableListFilterState(state) && !!state.isSearching,
    );

    // A search aggregate can briefly drop a facet section. Popping the
    // stack remounts the sheet and dismisses the iOS keyboard.
    const isStaleView =
        currentView !== null &&
        currentSections.length === 0 &&
        !isSearchInFlight;
    const view = isStaleView ? null : currentView;

    useEffect(() => {
        if (isStaleView) {
            setViewStack([]);
        }
    }, [isStaleView]);

    const viewKey =
        view === null
            ? 'root'
            : view.type === 'moreFilters'
              ? 'more-filters'
              : `section-${view.sectionTitle}`;

    useEffect(() => {
        scrollRef.current?.scrollTo(0, 0);
    }, [viewKey]);

    const viewTitle =
        view === null
            ? 'Refine'
            : view.type === 'moreFilters'
              ? MORE_FILTERS_TITLE
              : view.sectionTitle;

    const totalCount = localFilterStates.totalCount ?? 0;
    const stagedCategory = localFilterStates.states.category;

    return (
        <>
            <SheetHeader variant="borderless">
                {view !== null && (
                    <button
                        type="button"
                        aria-label="Back to all filters"
                        onClick={popView}
                        className="absolute top-4 left-4 flex size-11 cursor-pointer items-center justify-center opacity-70 transition-opacity hover:opacity-100"
                    >
                        <ChevronLeft className="size-6" />
                    </button>
                )}
                <SheetTitle>Refine</SheetTitle>
                <SheetDescription className="hidden" />
            </SheetHeader>

            <div
                ref={scrollRef}
                className="h-[70svh] max-h-[70svh] scrollbar-hidden w-dvw overflow-y-scroll [overflow-anchor:none]"
            >
                <div
                    key={viewKey}
                    className={cn(
                        // divide-y draws a subtle separator between the
                        // groups: chips / category / sort / filters on the
                        // root, chips / section content in sub-views. Each
                        // group carries symmetric padding (*:py-3) so the
                        // separators breathe; pt-2 adds leading air under
                        // the borderless header.
                        'mx-5 flex animate-in flex-col divide-y divide-gray-900/10 px-1 pt-2 duration-200 fade-in *:py-3 motion-reduce:animate-none',
                        lastNavDirection.current === 'push'
                            ? 'slide-in-from-right-4'
                            : 'slide-in-from-left-4',
                    )}
                >
                    {view === null ? (
                        <>
                            <RefineChipsRow
                                filterStates={localFilterStates}
                                config={config}
                                onUpdateFilterState={onUpdateFilterState}
                                onUpdateFacetFilterState={
                                    onUpdateFacetFilterState
                                }
                                onClearFilters={onClearFilters}
                            />
                            {categoryTree &&
                                stagedCategory &&
                                isCategoryFilterState(stagedCategory) && (
                                    <FiltersModalCategoryTree
                                        categoryState={stagedCategory}
                                        rootLabel={categoryTree.rootLabel}
                                        onStageCategory={onStageCategory}
                                    />
                                )}
                            {showSorting && (
                                <RefineSortPills
                                    sort={localFilterStates.sortBy}
                                    onSortChange={onStageSort}
                                />
                            )}
                            {browseMode && (
                                <RefineBrowseModePills {...browseMode} />
                            )}
                            <section aria-label="Filters">
                                <div className="flex min-h-11 items-center text-base font-medium">
                                    Filters
                                </div>
                                <RefineSectionList
                                    config={config}
                                    facetSections={facetSections}
                                    filterStates={localFilterStates}
                                    onUpdateFilterState={onUpdateFilterState}
                                    onUpdateFacetFilterState={
                                        onUpdateFacetFilterState
                                    }
                                    onAlphabeticalGrouping={
                                        onAlphabeticalGrouping
                                    }
                                    onDrillIn={pushView}
                                />
                            </section>
                        </>
                    ) : (
                        <>
                            <RefineChipsRow
                                filterStates={localFilterStates}
                                config={config}
                                onUpdateFilterState={onUpdateFilterState}
                                onUpdateFacetFilterState={
                                    onUpdateFacetFilterState
                                }
                                onClearFilters={onClearFilters}
                            />
                            {categoryTree &&
                                stagedCategory &&
                                isCategoryFilterState(stagedCategory) && (
                                    <FiltersModalCategoryTree
                                        categoryState={stagedCategory}
                                        rootLabel={categoryTree.rootLabel}
                                        onStageCategory={onStageCategory}
                                        showChildren={false}
                                    />
                                )}
                            <section aria-label={viewTitle}>
                                <div className="flex min-h-11 items-center text-base font-medium">
                                    {viewTitle}
                                </div>
                                <RefineDrillInView
                                    sections={currentSections}
                                    showSectionTitles={
                                        view.type === 'moreFilters'
                                    }
                                    filterStates={localFilterStates}
                                    onUpdateFilterState={onUpdateFilterState}
                                    onUpdateFacetFilterState={
                                        onUpdateFacetFilterState
                                    }
                                    onAlphabeticalGrouping={
                                        onAlphabeticalGrouping
                                    }
                                />
                            </section>
                        </>
                    )}
                </div>
            </div>

            <SheetFooter variant="borderless">
                <RoccoButton
                    color="secondary"
                    inverted
                    className="w-full"
                    onClick={onApply}
                    isLoading={isLoading}
                    disabled={totalCount === 0}
                >
                    {totalCount > 0
                        ? `Show ${formatToThousandsDelimiter(totalCount)} ${pluralize(totalCount, resultName ?? config.resultName, true)}`
                        : 'No exact matches'}
                </RoccoButton>
            </SheetFooter>
        </>
    );
};
